import { IDatabaseMigration } from "client/Database/migrator";
import { isNotNil } from "client/Misc/utils";

export const makeDataPersistentForMultipleUsers: IDatabaseMigration = {
	upgrade: async (database: IDBDatabase, transaction: IDBTransaction): Promise<void> => {
		database.createObjectStore("singleUserZustandStores", { keyPath: "name" });

		const userId: number | null = await getUserId(transaction);

		if (database.objectStoreNames.contains("settings")) {
			database.deleteObjectStore("settings");
		}

		if (isNotNil(userId)) {
			await moveDataToTheSingleUserStore(transaction, userId);
		}

		database.deleteObjectStore("zustandStores");
		database.createObjectStore("zustandStores", { keyPath: ["name", "userId"] });
	}
};

type Settings = {
	key: "singleton";
	userId: number;
};

function getUserId(transaction: IDBTransaction): Promise<number | null> {
	if (!transaction.objectStoreNames
		.contains("settings")
	) {
		return Promise.resolve(null);
	}

	const settingsStore: IDBObjectStore = transaction.objectStore("settings");
	const request: IDBRequest<Settings> = settingsStore.get("singleton");

	return resolveRequest(request)
		.then(s => s.userId)
		.catch(_ => null);
}

async function moveDataToTheSingleUserStore(transaction: IDBTransaction, userId: number): Promise<void> {
	const oldObjectStore: IDBObjectStore = transaction.objectStore("zustandStores");
	const request: IDBRequest<OldZustandStore[]> = oldObjectStore.getAll();
	const oldData: readonly OldZustandStore[] = await resolveRequest(request);

	const newData: readonly SingleUserZustandStore[] = oldData.map(
		(store: OldZustandStore): SingleUserZustandStore => ({
			...store,
			userId
		})
	);

	const newObjectStore: IDBObjectStore = transaction.objectStore("singleUserZustandStores");

	for (const storeData of newData) {
		const request: IDBRequest<IDBValidKey> = newObjectStore.add(storeData);

		await resolveRequest(request);
	}
}

function resolveRequest<TResult>(request: IDBRequest<TResult>): Promise<TResult> {
	return new Promise<TResult>(
		(resolve, reject) => {
			request.onsuccess = (): void => {
				resolve(request.result);
			};

			request.onerror = (): void => {
				reject(request.error);
			};
		}
	);
}

type OldZustandStore = {
	name: string;
	data: string;
};

type SingleUserZustandStore = {
	name: string;
	data: string;
	userId: number;
};